interface IWindow extends Window {
    routes: Record<string, string>;
}

class Routes {
    private routes: Record<string, string>;
    private route: string = '';

    constructor(name: string, params: Record<string, string | number> = {}) {
        this.routes = (window as unknown as IWindow).routes;
        this.getRoute(name, params);
    }

    private getRoute(
        name: string,
        params: Record<string, string | number> = {}
    ): void {
        if (!Object.keys(this.routes).includes(name)) {
            throw new Error(`Route ${name} is not in the routes list`);
        }

        let routePattern = this.routes[name];
        const usedParams = new Set<string>();

        // Handle Django regex patterns like (?P<slug>[-a-zA-0-9_]+)
        // Replace them with the actual parameter values
        for (const [key, value] of Object.entries(params)) {
            // Django named group pattern: (?P<param_name>regex_pattern)
            const djangoPattern = new RegExp(`\\(\\?P<${key}>[^)]+\\)`, 'g');
            if (djangoPattern.test(routePattern)) {
                routePattern = routePattern.replace(
                    djangoPattern,
                    String(value)
                );
                usedParams.add(key);
                continue;
            }

            // Try Django style with type hints (e.g., <int:id>, <slug:slug>)
            const typedPattern = new RegExp(`<\\w+:${key}>`, 'g');
            if (typedPattern.test(routePattern)) {
                routePattern = routePattern.replace(
                    typedPattern,
                    String(value)
                );
                usedParams.add(key);
                continue;
            }

            // Try Django style without type (e.g., <id>, <slug>)
            const simplePattern = new RegExp(`<${key}>`, 'g');
            if (simplePattern.test(routePattern)) {
                routePattern = routePattern.replace(
                    simplePattern,
                    String(value)
                );
                usedParams.add(key);
                continue;
            }

            // Try colon style (e.g., :id)
            const colonPattern = new RegExp(`:${key}\\b`, 'g');
            if (colonPattern.test(routePattern)) {
                routePattern = routePattern.replace(
                    colonPattern,
                    String(value)
                );
                usedParams.add(key);
                continue;
            }

            // Try bracket style (e.g., {id})
            const bracketPattern = new RegExp(`\\{${key}\\}`, 'g');
            if (bracketPattern.test(routePattern)) {
                routePattern = routePattern.replace(
                    bracketPattern,
                    String(value)
                );
                usedParams.add(key);
            }
        }

        // Build query string from unused parameters
        const queryParams = Object.entries(params)
            .filter(([key]) => !usedParams.has(key))
            .map(
                ([key, value]) =>
                    `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
            )
            .join('&');

        // Construct the final route
        this.route = `/${routePattern}${queryParams ? `?${queryParams}` : ''}`;
    }

    public current(): boolean {
        return this.route === window.location.pathname;
    }

    public toString(): string {
        return this.route;
    }
}

const useRoutes = () => {
    const route = (
        name: string,
        params: Record<string, string | number> = {}
    ): Routes | string => {
        return new Routes(name, params);
    };

    return { route };
};

export default useRoutes;
